/* eslint-disable @typescript-eslint/restrict-template-expressions */
import styled from 'styled-components';
import palette from '@styles/theme/palette';

// Adaptive tokens (CSS vars) come from `@styles/global` based on the creator's selected
// `campaign.style.background` (surface, text, borders) and `responseButtonBackground` (accent).
// The accent governs active states (timestamp, line border, "New" badge, Pill on, Spinner)
// so the panel harmonizes with the creator's CTA color instead of stranding snozzberry on a
// page themed in another color. Success mint is a fixed semantic color (copy confirmation).
const COLORS = {
  accent: `var(--global-accent, ${palette.primary.main})`,
  successMint: palette.caribbeanGreen,
  surface: `var(--global-background, ${palette.bianca})`,
  border: `var(--global-border, ${palette.pampas})`,
  hover: `var(--global-hover, ${palette.springWood})`,
  activeWash: `var(--global-active-wash, ${palette.lightSendsparkPurple})`,
  fg: `var(--global-foreground, ${palette.common.black})`,
  fgMuted: `var(--global-foreground-muted, ${palette.naturalGray})`,
  fgSubtle: `var(--global-foreground-subtle, ${palette.friarGray})`,
  scrollbarThumb: `var(--global-scrollbar-thumb, ${palette.swissCoffee})`,
};
const FONT_MONO = "ui-monospace, 'SF Mono', Menlo, Consolas, monospace";
const FAST = '150ms cubic-bezier(0.4, 0, 0.2, 1)';

// Shared height target so the standalone Panel and the SidePanel's Card stay aligned
// vertically. Exported so the SidePanel can derive its `min-height` from it (= body + tab bar)
// instead of carrying an unrelated magic number.
export const PANEL_BODY_MAX_HEIGHT = 700;

export const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  background: ${COLORS.surface};
  border: 1px solid ${COLORS.border};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 4px 1px rgba(193, 199, 205, 0.48);
`;

export const TabHeader = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
  padding: 6px 6px 0;
  background: ${COLORS.surface};
  border-bottom: 1px solid ${COLORS.border};
  flex-shrink: 0;
`;

export const Tab = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  margin-bottom: -1px;
  font-family: Lexend, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${COLORS.accent};
  border-bottom: 2px solid ${COLORS.accent};
`;

// "AI" tab badge (design views G/H), shared with the SidePanel tab bar. On an active
// (or standalone) tab it sits on the accent with everything white; on an inactive tab
// it drops to the brand ink with a light-purple sparkle.
export const AiBadge = styled.span<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${palette.common.white};
  background: ${({ active = true }) => (active ? COLORS.accent : palette.haiti)};
  padding: 2px 6px;
  border-radius: 4px;
  transition: background ${FAST};

  svg {
    color: ${({ active = true }) => (active ? palette.common.white : palette.primary.light)};
  }
`;

// ── AI source header (design views G/H) — tells the viewer this transcript is AI-generated ──
export const SourceHeader = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 14px;
  background: linear-gradient(90deg, ${COLORS.activeWash}, transparent 70%), ${COLORS.surface};
  border-bottom: 1px solid ${COLORS.border};
  flex-shrink: 0;
`;

export const SourceIcon = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 4px;
  background: ${COLORS.accent};
  color: ${palette.common.white};
  display: grid;
  place-items: center;
  box-shadow: 0 2px 7px rgba(97, 64, 255, 0.32);
`;

export const SourceLabel = styled.span`
  font-family: Lexend, sans-serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${COLORS.fg};
`;

export const SourceInfoButton = styled.button`
  margin-left: auto;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${COLORS.fgSubtle};
  display: inline-grid;
  place-items: center;
  padding: 2px;
  border-radius: 4px;
  transition: color ${FAST};

  &:hover {
    color: ${COLORS.accent};
  }
`;

export const SourcePopover = styled.div`
  position: absolute;
  top: calc(100% - 2px);
  right: 12px;
  z-index: 6;
  width: 248px;
  background: ${palette.haiti};
  color: ${palette.common.white};
  font-family: Lexend, sans-serif;
  font-size: 11px;
  line-height: 17px;
  padding: 10px 12px;
  border-radius: 8px;
  box-shadow: 0 12px 28px rgba(16, 11, 43, 0.28);
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 14px;
  border-bottom: 1px solid ${COLORS.border};
  background: ${COLORS.surface};
  flex-shrink: 0;
`;

export const ToolButton = styled.button<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 6px 10px;
  font-family: Lexend, sans-serif;
  font-size: 12px;
  font-weight: ${({ active }) => (active ? 700 : 500)};
  color: ${({ active }) => (active ? COLORS.accent : COLORS.fgMuted)};
  cursor: pointer;
  transition: background ${FAST}, color ${FAST};

  &:hover {
    background: ${COLORS.hover};
    color: ${({ active }) => (active ? COLORS.accent : COLORS.fg)};
  }
`;

export const ToolSpacer = styled.div`
  flex: 1;
`;

export const Pill = styled.span<{ on?: boolean }>`
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${({ on }) => (on ? COLORS.accent : COLORS.hover)};
  color: ${({ on }) => (on ? palette.common.white : COLORS.fgSubtle)};
  padding: 1px 6px;
  border-radius: 999px;
`;

export const ScrollArea = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  max-height: ${PANEL_BODY_MAX_HEIGHT}px;
  

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${COLORS.scrollbarThumb};
    border-radius: 999px;
    border: 2px solid ${COLORS.surface};
  }
`;

export const Inner = styled.div`
  /* No height cap: Inner is the scrolled content; ScrollArea (flex: 1; overflow-y: auto)
     owns the bounds. Capping Inner clipped long transcripts and left whitespace inside
     the SidePanel Card when this body was embedded under a tab. */
  padding: 8px;
`;

export type LineState = 'past' | 'active' | 'future';

export const Line = styled.button<{ state: LineState }>`
  display: grid;
  grid-template-columns: 44px 1fr;
  align-items: start;
  gap: 12px;
  width: 100%;
  background: ${({ state }) => (state === 'active' ? COLORS.activeWash : 'transparent')};
  border: none;
  text-align: left;
  padding: 8px 14px;
  border-radius: 6px;
  margin: 2px 0;
  font-family: Lexend, sans-serif;
  cursor: pointer;
  position: relative;
  transition: background ${FAST}, opacity ${FAST};

  &:hover {
    background: ${({ state }) => (state === 'active' ? COLORS.activeWash : COLORS.hover)};
  }
  &:hover .ts {
    color: ${COLORS.accent};
  }

  &::before {
    content: '';
    position: absolute;
    left: 4px;
    top: 8px;
    bottom: 8px;
    width: 3px;
    border-radius: 999px;
    background: ${({ state }) => (state === 'active' ? COLORS.accent : 'transparent')};
  }
`;

export const Timestamp = styled.span<{ state: LineState }>`
  font-family: ${FONT_MONO};
  font-size: 11px;
  padding-top: 3px;
  letter-spacing: 0.01em;
  font-variant-numeric: tabular-nums;
  font-weight: ${({ state }) => (state === 'active' ? 700 : 400)};
  transition: color ${FAST};
  color: ${({ state }) => {
    if (state === 'past') return COLORS.fgSubtle;
    if (state === 'active') return COLORS.accent;
    return COLORS.fgMuted;
  }};
`;

export const Text = styled.span<{ state: LineState }>`
  font-size: 14px;
  line-height: 22px;
  transition: color ${FAST};
  font-weight: ${({ state }) => (state === 'active' ? 500 : 400)};
  color: ${({ state }) => {
    if (state === 'past') return COLORS.fgSubtle;
    if (state === 'future') return COLORS.fgMuted;
    return COLORS.fg;
  }};
  opacity: ${({ state }) => (state === 'past' ? 0.62 : 1)};
`;

export const StatePlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 100%;
  min-height: 200px;
  padding: 32px 24px;
  text-align: center;
  color: ${COLORS.fgMuted};
  font-size: 14px;
  line-height: 20px;
`;

export const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${COLORS.scrollbarThumb};
  border-top-color: ${COLORS.accent};
  animation: tp-spin 0.8s linear infinite;

  @keyframes tp-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
